// fonts.js — "Night Build" design system for GoGameDev3D.
//
// Concept: the site feels like a friendly game editor at night.
// One canvas (deep navy), one structural accent (Godot blue), one action
// color (coin gold — the "press start" / XP color), per-course gem accents.
//
// Typography (all Hebrew-safe — whole-string styling only, never per-letter):
//   Display: "Suez One"  — Hebrew+Latin slab. Bold, warm, poster-like.
//   Body:    "Assistant" — Hebrew+Latin, friendly, crisp at small sizes.
//   HUD:     "Silkscreen" — pixel font, Latin only. Used ONLY for the logo
//            and tiny HUD-style labels (prices, level tags). Never for Hebrew.

export const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Suez+One&family=Assistant:wght@400;600;700;800&family=Silkscreen:wght@400;700&display=swap';

/**
 * The two palettes. Every text pair here is checked against the *worst-case*
 * surface of its own theme — the lightest panel in dark mode, the darkest
 * surface in light mode — so a token can't pass on one background and fail on
 * another. Ratios are noted per line; the floor is 4.5:1 for text and 3:1 for
 * focus rings and other non-text indicators.
 */
export const PALETTES = {
  dark: {
    bg:        '#070B16',                  // deep night navy (3D scene sits in this)
    panel:     '#0E1526',                  // solid editor panel — no heavy glass
    panelUp:   '#131C33',                  // raised panel (hover/inputs)
    line:      'rgba(125,156,215,0.16)',
    lineSoft:  'rgba(125,156,215,0.09)',

    godot:     '#478CBF',                  // 4.7:1 — structural: links, labels
    godotSoft: 'rgba(71,140,191,0.16)',
    godotLight:'#9DC3E6',                  // 10.4:1 — quiet blue for badges
    gold:      '#FFC857',                  // action: CTAs, highlights
    goldInk:   '#211603',                  // 11.6:1 on gold
    focus:     '#FFC857',                  // 9.6:1 vs panelUp
    green:     '#34D399',
    red:       '#F87171',

    text:      '#EDF2FB',                  // 15.1:1
    muted:     '#A3B1CA',                  // 7.8:1
    faint:     '#7C8BA8',                  // 4.9:1

    panelGlass:'rgba(14,21,38,0.82)',      // translucent panel over the 3D scene
    goldSoft:  'rgba(255,200,87,0.08)',
    goldLine:  'rgba(255,200,87,0.35)',
    goldInkSoft:'#E7C67C',                 // notice text on the soft gold wash
    scrim:     '7,11,22',                  // page bg as raw RGB, for gradients
    navBg:     'rgba(7,11,22,0.88)',
    navBgScrolled:'rgba(7,11,22,0.97)',
    scrollThumb:'#2B3A5C',
    scrollHover:'#3D5180',
    dot:       'rgba(125,156,215,0.10)',   // .gg-bg grid
    disabled:  '#3A4663',
    disabledInk:'#8FA0BC',
    linkHover: '#6FB3DF',
  },

  /**
   * Light — Godot's editor palette: a cool near-white base carrying the brand
   * blue. The blue is darkened from #478CBF to #2F6FA3 because the brand value
   * only reaches 2.6:1 on white and would fail as link text.
   */
  light: {
    bg:        '#EAF4FB',                  // airy blue-white canvas
    panel:     '#FAFDFF',                  // near-white cards retain clear lift
    panelUp:   '#E2F0F9',                  // blue-tinted controls / raised areas
    line:      'rgba(29,92,143,0.22)',
    lineSoft:  'rgba(29,92,143,0.11)',

    godot:     '#2F6FA3',                  // 4.8:1
    godotSoft: 'rgba(47,111,163,0.14)',
    godotLight:'#1D5C8F',                  // 6.3:1 — the dark twin on light
    gold:      '#FFC857',                  // still the CTA fill; ink stays dark
    goldInk:   '#211603',                  // 11.6:1 on gold
    focus:     '#1D5C8F',                  // 6.3:1 — gold rings are 1.4:1 here
    green:     '#0C7350',                  // 5.2:1
    red:       '#C0342B',                  // 5.0:1

    text:      '#0E1A2B',                  // 15.6:1
    muted:     '#46566F',                  // 6.6:1
    faint:     '#5A6B84',                  // 4.8:1

    panelGlass:'rgba(248,252,255,0.92)',   // cool glass over the 3D scenes
    goldSoft:  'rgba(180,120,0,0.10)',
    goldLine:  'rgba(140,95,0,0.42)',
    goldInkSoft:'#7A5300',                 // 5.9:1 on the light gold wash
    scrim:     '234,244,251',
    navBg:     'rgba(232,243,251,0.92)',
    navBgScrolled:'rgba(232,243,251,0.98)',
    scrollThumb:'#9FBFD5',
    scrollHover:'#79A6C4',
    dot:       'rgba(47,111,163,0.13)',
    disabled:  '#CBDFEC',
    disabledInk:'#5A6B84',
    linkHover: '#17527F',
  },
};

/** Tokens that stay literal: they are fed to three.js, which cannot parse
    var(). They are also brand colours, so they do not change per theme. */
const FIXED = {
  python:    '#FFC857',
  godotBlue: '#5CA7DD',
  web3d:     '#34D9B5',
  minecraft: '#7CBD4A',
};

/** Contrast-safe stand-ins for the course accents when used as *text* on a
    light surface — the raw gems land at 1.5–2.6:1 there. */
export const ACCENT_INK_LIGHT = {
  python:    '#8A6100',                    // 5.5:1
  godotBlue: '#1F6FA8',                    // 5.4:1
  web3d:     '#0B7C64',                    // 5.1:1
  minecraft: '#3E7A1E',                    // 5.2:1
};

const cssVar = (name) => `var(--gg-${name})`;

/**
 * Design tokens — single source of truth.
 *
 * The colour values are `var(--gg-*)` references rather than literals, so the
 * ~166 places that already read them (inline styles and injected stylesheets
 * alike) re-theme themselves with no changes at the call site. The literals
 * live in PALETTES above and are published as custom properties below.
 */
export const T = {
  ...Object.fromEntries(Object.keys(PALETTES.dark).map((k) => [k, cssVar(k)])),
  ...FIXED,

  // fonts
  fontDisplay: '"Suez One", "Rubik", "Segoe UI", system-ui, serif',
  fontBody:    '"Assistant", "Heebo", "Segoe UI", system-ui, sans-serif',
  fontHud:     '"Silkscreen", "Courier New", monospace',

  // ── legacy keys (kept so untouched files keep working) ──
  surface:     cssVar('panel'),
  border:      cssVar('line'),
  borderWeak:  cssVar('lineSoft'),
  indigo:      cssVar('godot'),
  violet:      FIXED.godotBlue,
  cyan:        FIXED.web3d,
  indigoLight: cssVar('godotLight'),
};

/* ── Theme ──────────────────────────────────────────────────────────────── */

export const THEME_STORAGE_KEY = 'gg-theme';
const THEME_EVENT = 'gg-themechange';

const paletteToCss = (palette) =>
  Object.entries(palette).map(([k, v]) => `--gg-${k}: ${v};`).join('\n      ');

/** The stored choice, or the OS preference when the visitor has not chosen. */
export const resolveInitialTheme = () => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* private mode / storage disabled — fall through to the OS preference */
  }
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

/** Applies a theme and tells the rest of the app (including the 3D scene). */
export const applyTheme = (theme) => {
  const next = theme === 'light' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', next);
  // Native form controls, scrollbars and the browser UI follow this.
  document.documentElement.style.colorScheme = next;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    /* not persisting is survivable; the theme still applies for this visit */
  }
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: next }));
  return next;
};

let themeTransitionActive = false;

const clearThemeTransitionState = (root) => {
  themeTransitionActive = false;
  root.classList.remove('gg-theme-transitioning');
};

/**
 * Reveal the next palette outwards from the toggle with a short-lived,
 * compositor-only colour veil. Deliberately do not use startViewTransition:
 * capturing a full-page snapshot pauses a live WebGL canvas and makes the
 * character and particles visibly freeze. Reduced-motion visitors switch
 * instantly.
 */
export const transitionTheme = (theme, origin = {}) => {
  const next = theme === 'light' ? 'light' : 'dark';
  const current = getTheme();
  if (next === current || themeTransitionActive) return Promise.resolve(current);

  const root = document.documentElement;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    || root.classList.contains('stop-animations');
  if (reduced) return Promise.resolve(applyTheme(next));

  const x = Number.isFinite(origin.x) ? origin.x : window.innerWidth / 2;
  const y = Number.isFinite(origin.y) ? origin.y : window.innerHeight / 2;
  const radius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y)
  ) + 32;

  themeTransitionActive = true;
  root.classList.add('gg-theme-transitioning');

  return new Promise((resolve) => {
    const veil = document.createElement('span');
    veil.className = 'gg-theme-wipe';
    veil.setAttribute('aria-hidden', 'true');
    veil.style.setProperty('--gg-theme-x', `${x}px`);
    veil.style.setProperty('--gg-theme-y', `${y}px`);
    veil.style.setProperty('--gg-theme-radius', `${radius}px`);
    // The curtain approximates the outgoing palette. The new live page is
    // applied underneath immediately, then revealed through the growing hole.
    veil.style.setProperty('--gg-theme-wipe-color', PALETTES[current].bg);
    veil.style.setProperty('--gg-theme-wipe-accent', PALETTES[current].godotSoft);
    document.body.appendChild(veil);
    applyTheme(next);

    requestAnimationFrame(() => {
      veil.classList.add('is-expanding');
      window.setTimeout(() => {
        veil.classList.add('is-settling');
        window.setTimeout(() => {
          veil.remove();
          clearThemeTransitionState(root);
          resolve(next);
        }, 180);
      }, 620);
    });
  });
};

export const getTheme = () =>
  document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';

export const onThemeChange = (handler) => {
  window.addEventListener(THEME_EVENT, handler);
  return () => window.removeEventListener(THEME_EVENT, handler);
};

/** Resolve a token to a real colour string — for three.js, which cannot read
    var(). Reads the live custom property, so it always matches the DOM. */
export const readToken = (name, fallback = '#000000') => {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(`--gg-${name}`)
    .trim();
  return value || fallback;
};

export const injectFonts = () => {
  if (!document.getElementById('gf-site')) {
    const l = document.createElement('link');
    l.id = 'gf-site';
    l.rel = 'stylesheet';
    l.href = FONTS_HREF;
    document.head.appendChild(l);
  }
  injectGlobalStyles();
};

/**
 * Global stylesheet — injected once. Carries the shared visual language so
 * every page composes the same primitives instead of re-inventing them:
 *
 *   .gg-bg        page background: faint dot-grid + one soft glow (the "viewport")
 *   .gg-node      scene-tree eyebrow: ◆── label  (the signature element)
 *   .gg-card      editor panel card
 *   .gg-select    editor "selection box" — gold corner brackets on hover/focus
 *   .gg-btn       primary action (coin gold)
 *   .gg-btn-ghost secondary action (outline)
 *   .gg-hud       tiny pixel HUD label (Latin only!)
 *   .gg-input     form field
 *   .gg-reveal    staggered page-load reveal (use --d for delay)
 */
const injectGlobalStyles = () => {
  if (document.getElementById('gg-global')) return;
  const s = document.createElement('style');
  s.id = 'gg-global';
  s.textContent = `
    /* ── Palettes ──
       Dark is the default so a document with no data-theme still renders the
       original design. Everything else in the app reads these through T. */
    :root {
      ${paletteToCss(PALETTES.dark)}
      color-scheme: dark;
    }
    :root[data-theme="light"] {
      ${paletteToCss(PALETTES.light)}
      color-scheme: light;
    }

    ::selection { background: rgba(255,200,87,0.35); color: ${T.goldInk}; }

    /* ── Reading comfort ──
       Hebrew sets with a tall, even x-height and no capital/lowercase rhythm,
       so it needs more leading than a Latin default to stay readable — and on
       a dark background generous leading matters even more, because light text
       on dark blooms optically and tight lines start to smear together. */
    body {
      line-height: 1.7;
      letter-spacing: 0.006em;
      color: ${T.text};
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    p { text-wrap: pretty; }
    h1, h2, h3, h4 { text-wrap: balance; line-height: 1.25; }
    /* Keep prose off over-long measures; ~70 characters is the comfort limit. */
    p, li { max-inline-size: 72ch; }

    html { scrollbar-width: thin; scrollbar-color: ${T.scrollThumb} ${T.bg}; }
    ::-webkit-scrollbar { width: 10px; }
    ::-webkit-scrollbar-track { background: ${T.bg}; }
    ::-webkit-scrollbar-thumb { background: ${T.scrollThumb}; border-radius: 8px; border: 2px solid ${T.bg}; }
    ::-webkit-scrollbar-thumb:hover { background: ${T.scrollHover}; }

    @keyframes ggReveal {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ggBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }

    /* ── Viewport background: dot grid + one quiet glow ── */
    .gg-bg {
      position: relative;
      background:
        radial-gradient(1100px 540px at 78% -8%, ${T.godotSoft}, transparent 64%),
        radial-gradient(${T.dot} 1px, transparent 1.5px) 0 0 / 26px 26px,
        ${T.bg};
    }
    :root[data-theme="light"] .gg-bg {
      background:
        radial-gradient(900px 520px at 88% -6%, rgba(92,167,221,0.24), transparent 66%),
        radial-gradient(760px 540px at 4% 72%, rgba(157,195,230,0.22), transparent 70%),
        radial-gradient(${T.dot} 1px, transparent 1.5px) 0 0 / 26px 26px,
        ${T.bg};
    }

    /* ── Signature: scene-tree node eyebrow ── */
    .gg-node {
      display: inline-flex; align-items: center; gap: 10px;
      font-family: ${T.fontBody};
      font-size: clamp(0.78rem, 2.4vw, 0.88rem);
      font-weight: 800; letter-spacing: 0.06em;
      color: ${T.godot};
    }
    .gg-node::before {
      content: ''; width: 9px; height: 9px; flex-shrink: 0;
      background: ${T.godot}; transform: rotate(45deg);
      box-shadow: 0 0 10px rgba(71,140,191,0.7);
    }
    .gg-node::after {
      content: ''; height: 1px; width: 42px; flex-shrink: 0;
      background: linear-gradient(90deg, ${T.godot}, transparent);
    }
    [dir="rtl"] .gg-node::after { background: linear-gradient(270deg, ${T.godot}, transparent); }

    /* ── Editor panel card ── */
    .gg-card {
      background: ${T.panel};
      border: 1px solid ${T.line};
      border-radius: 16px;
    }

    /* ── Editor selection box: gold corner brackets ── */
    .gg-select { position: relative; transition: transform .3s cubic-bezier(.2,.8,.2,1); }
    .gg-select::before, .gg-select::after,
    .gg-select > .gg-corners::before, .gg-select > .gg-corners::after {
      content: ''; position: absolute; width: 14px; height: 14px;
      border: 2px solid ${T.gold}; opacity: 0;
      transition: opacity .25s ease, transform .25s ease;
      pointer-events: none; z-index: 4;
    }
    .gg-select::before              { top: -5px;    right: -5px;  border-left: none;  border-bottom: none; border-radius: 0 6px 0 0; transform: translate(-6px, 6px); }
    .gg-select::after               { bottom: -5px; left: -5px;   border-right: none; border-top: none;    border-radius: 0 0 0 6px; transform: translate(6px, -6px); }
    .gg-select > .gg-corners::before{ top: -5px;    left: -5px;   border-right: none; border-bottom: none; border-radius: 6px 0 0 0; transform: translate(6px, 6px); }
    .gg-select > .gg-corners::after { bottom: -5px; right: -5px;  border-left: none;  border-top: none;    border-radius: 0 0 6px 0; transform: translate(-6px, -6px); }
    .gg-select:hover, .gg-select:focus-within { transform: translateY(-4px); }
    .gg-select:hover::before, .gg-select:hover::after,
    .gg-select:hover > .gg-corners::before, .gg-select:hover > .gg-corners::after,
    .gg-select:focus-within::before, .gg-select:focus-within::after,
    .gg-select:focus-within > .gg-corners::before, .gg-select:focus-within > .gg-corners::after {
      opacity: 1; transform: translate(0, 0);
    }

    /* ── Buttons ── */
    .gg-btn {
      display: inline-flex; align-items: center; justify-content: center; gap: 10px;
      /* border-box, or the 48px touch-target minimum stacks on top of the
         padding and every button on the site renders 76px tall. Controls also
         opt out of the body's prose leading — buttons are not paragraphs. */
      box-sizing: border-box; line-height: 1.2;
      padding: 14px 28px; border: none; border-radius: 12px; cursor: pointer;
      background: ${T.gold}; color: ${T.goldInk};
      font-family: ${T.fontBody}; font-weight: 800;
      font-size: clamp(0.95rem, 2.8vw, 1.05rem);
      text-decoration: none;
      box-shadow: 0 6px 24px rgba(255,200,87,0.28), inset 0 -2px 0 rgba(0,0,0,0.18);
      transition: transform .2s ease, box-shadow .2s ease, background .2s ease;
      min-height: 48px;
    }
    .gg-btn:hover  { background: #FFD477; transform: translateY(-2px); box-shadow: 0 12px 32px rgba(255,200,87,0.38), inset 0 -2px 0 rgba(0,0,0,0.18); }
    .gg-btn:active { transform: translateY(0); }
    .gg-btn:disabled { background: ${T.disabled}; color: ${T.disabledInk}; box-shadow: none; cursor: not-allowed; }
    .gg-btn:focus-visible { outline: 3px solid ${T.focus}; outline-offset: 3px; }

    .gg-btn-ghost {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      box-sizing: border-box; line-height: 1.2;
      padding: 12px 24px; border-radius: 12px; cursor: pointer;
      background: transparent; border: 1.5px solid ${T.line};
      color: ${T.text}; font-family: ${T.fontBody}; font-weight: 700;
      font-size: clamp(0.88rem, 2.6vw, 0.95rem); text-decoration: none;
      transition: border-color .2s ease, background .2s ease, transform .2s ease;
      min-height: 44px;
    }
    .gg-btn-ghost:hover { border-color: ${T.godot}; background: ${T.godotSoft}; transform: translateY(-1px); }
    .gg-btn-ghost:focus-visible { outline: 2px solid ${T.focus}; outline-offset: 3px; }

    /* ── Tiny pixel HUD label (Latin only) ── */
    .gg-hud {
      font-family: ${T.fontHud};
      font-size: 0.72rem; letter-spacing: 0.08em;
      color: ${T.faint}; text-transform: uppercase;
    }

    /* ── Form fields ── */
    .gg-input {
      width: 100%; box-sizing: border-box;
      padding: 14px 44px;
      background: ${T.panelUp};
      border: 1px solid ${T.line}; border-radius: 12px;
      color: ${T.text};
      font-family: ${T.fontBody}; font-size: 1rem;
      outline: none; transition: border-color .25s ease, box-shadow .25s ease;
    }
    .gg-input::placeholder { color: ${T.faint}; }
    .gg-input:focus { border-color: ${T.focus}; box-shadow: 0 0 0 3px rgba(255,200,87,0.18); }
    .gg-input:disabled { opacity: 0.55; cursor: not-allowed; }
    select.gg-input { appearance: none; -webkit-appearance: none; cursor: pointer; }
    select.gg-input option { background: ${T.panel}; color: ${T.text}; }

    /* ── Page-load reveal (set --d per element for stagger) ── */
    .gg-reveal { animation: ggReveal .6s cubic-bezier(.2,.8,.2,1) backwards; animation-delay: var(--d, 0s); }

    /* Links */
    .gg-link { color: ${T.godot}; font-weight: 700; text-decoration: none; }
    .gg-link:hover { color: ${T.linkHover}; text-decoration: underline; text-underline-offset: 3px; }
    .gg-link:focus-visible { outline: 2px solid ${T.focus}; outline-offset: 2px; border-radius: 3px; }

    /* Reduced motion (IS 5568 / WCAG 2.3.3) */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    }
  `;
  document.head.appendChild(s);
};

// Inject at import time, not just from each page's effect. Effects run after
// the first paint, so every styled element used to render unstyled for a frame
// and then transition into place — buttons visibly faded up from transparent,
// and contrast checkers sampled them mid-fade. Pages still call injectFonts();
// both halves are idempotent.
if (typeof document !== 'undefined') {
  // Theme first, then the stylesheet that reads it — both before React's first
  // paint, so the page never flashes the wrong palette.
  applyTheme(resolveInitialTheme());
  injectFonts();
}
